import { useEffect, useState } from 'react';
import { debug } from '../utils/debug';
import type { TelegramWebApp } from '../types';

interface UseTelegramReturn {
  tg: TelegramWebApp | null;
  user: TelegramWebApp['initDataUnsafe']['user'] | null;
  isReady: boolean;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramWebApp['themeParams'];
}

export const useTelegram = (): UseTelegramReturn => {
  const [isReady, setIsReady] = useState(false);
  const [tg, setTg] = useState<TelegramWebApp | null>(null);

  useEffect(() => {
    // Debug logging for Telegram SDK availability
    debug.log('🔍 useTelegram - SDK check:', {
      windowTelegram: !!window.Telegram,
      webApp: !!window.Telegram?.WebApp,
      initData: window.Telegram?.WebApp?.initData,
      initDataUnsafe: window.Telegram?.WebApp?.initDataUnsafe,
      user: window.Telegram?.WebApp?.initDataUnsafe?.user,
    });

    const initTelegram = () => {
      if (window.Telegram?.WebApp) {
        const telegram = window.Telegram.WebApp;
        setTg(telegram);
        
        debug.log('✅ useTelegram - Initializing Telegram WebApp:', {
          version: telegram.version,
          platform: telegram.platform,
          colorScheme: telegram.colorScheme,
          initData: telegram.initData,
          user: telegram.initDataUnsafe?.user,
        });
        
        // Initialize Telegram WebApp
        telegram.ready();
        telegram.expand();
        
        setIsReady(true);

        // Hide main button by default
        telegram.MainButton.hide();
        
        debug.log('✅ useTelegram - WebApp initialized successfully');
      } else {
        debug.warn('⚠️ useTelegram - Telegram WebApp is not available, retrying...');
        // Retry after short delay
        setTimeout(initTelegram, 100);
      }
    };

    // Initial check
    initTelegram();
  }, []);

  return {
    tg,
    user: tg?.initDataUnsafe?.user || null,
    isReady,
    colorScheme: tg?.colorScheme || 'light',
    themeParams: tg?.themeParams || {},
  };
};

// Hook for haptic feedback
export const useHapticFeedback = () => {
  const { tg } = useTelegram();

  const impact = (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.impactOccurred(style);
    }
  };

  const notification = (type: 'error' | 'success' | 'warning') => {
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.notificationOccurred(type);
    }
  };

  const selection = () => {
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.selectionChanged();
    }
  };

  return { impact, notification, selection };
};

// Hook for main button control
export const useMainButton = () => {
  const { tg } = useTelegram();

  const show = (text: string, onClick: () => void) => {
    if (tg?.MainButton) {
      tg.MainButton.setText(text);
      tg.MainButton.onClick(onClick);
      tg.MainButton.show();
    }
  };

  const hide = () => {
    if (tg?.MainButton) {
      tg.MainButton.hide();
    }
  };

  const showProgress = () => {
    if (tg?.MainButton) {
      tg.MainButton.showProgress();
    }
  };

  const hideProgress = () => {
    if (tg?.MainButton) {
      tg.MainButton.hideProgress();
    }
  };

  return { show, hide, showProgress, hideProgress };
}; 